import React, { useState, useEffect } from 'react';

// Bootstrap
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

// Custom CSS
import './styles/variables.css';
import './styles/mobile.css';
import './styles/desktop.css';
import './styles/mobile-light.css'; // Light theme (default)
import './styles/mobile-dark.css';  // Dark theme (when .theme-dark class)
import './styles/updown.css';
import './App.css';

// Contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TournamentProvider, useTournament } from './contexts/TournamentContext';
import { ToastProvider } from './contexts/ToastContext';

// Components
import LoginModal from './components/modals/LoginModal';
import SignOutModal from './components/modals/SignOutModal';
import TournamentListModal from './components/modals/TournamentListModal';
import QRCodeModal from './components/modals/QRCodeModal';
import ExportPDFModal from './components/modals/ExportPDFModal';
import ConfirmModal from './components/modals/ConfirmModal';
import AuthRequiredModal from './components/modals/AuthRequiredModal';
import ToastContainer from './components/common/ToastContainer';
import LoginBanner from './components/common/LoginBanner';

// Pages
import ScrollWizardPage from './pages/ScrollWizardPage';

// Config
import DEFAULT_CONFIG from './config/defaultConfig.json';

// Services
import firebaseSyncService from './services/firebaseSync';

// Hooks
import useFirebaseAutoSave from './hooks/useFirebaseAutoSave';

function AppContent() {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showTournamentListModal, setShowTournamentListModal] = useState(false);
  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  const [showExportPDFModal, setShowExportPDFModal] = useState(false);
  const [showAuthRequiredModal, setShowAuthRequiredModal] = useState(false);
  const [signOutLoading, setSignOutLoading] = useState(false);
  const [loadingTournament, setLoadingTournament] = useState(false);

  const { currentUser, loading, guestMode, isSignedIn, signOut, switchToLoginMode, handleAuthCallback } = useAuth();
  const { importState, reset, exportState } = useTournament();

  // Auto-save tournament to Firestore when changes occur
  const { isSaving, lastSaveTime } = useFirebaseAutoSave(exportState(), isSignedIn());

  // Handle auth callback token from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (token) {
      // Clean the URL immediately
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);

      handleAuthCallback(token).then((result) => {
        if (!result.success) {
          console.error('Auth callback failed:', result.error);
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check authentication on mount
  useEffect(() => {
    if (!loading) {
      if (!isSignedIn() && !guestMode) {
        setShowLoginModal(true);
      } else {
        setShowLoginModal(false);
        if (isSignedIn()) {
          // User is signed in, try to load last tournament
          loadLastTournament();
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, currentUser, guestMode]);

  const loadLastTournament = async () => {
    if (!currentUser) return;

    setLoadingTournament(true);
    try {
      // If there's a guest tournament in progress, save it first before loading from Firebase
      const currentState = exportState();
      const hasGuestData = currentState && (
        currentState.scheduleGenerated === true ||
        currentState.tournamentStarted === true
      );

      if (hasGuestData && !firebaseSyncService.currentTournamentId) {
        const savedId = await firebaseSyncService.createNewTournament(
          currentState,
          currentUser.email,
          currentState.config?.tournamentName || null
        );
        if (savedId) {
          console.log('💾 Tournoi invité sauvegardé sur Firebase:', savedId);
        }
        // Keep the current in-memory state, do not overwrite with Firebase data
        return;
      }

      // Check if there's a currentTournamentId in localStorage
      const savedTournamentId = localStorage.getItem('currentTournamentId');

      if (savedTournamentId) {
        // Load specific tournament
        const data = await firebaseSyncService.loadTournament(savedTournamentId, currentUser.email);
        if (data && data.state) {
          console.log('📋 Tournoi chargé - ID:', data.id || savedTournamentId, '| Nom:', data.state?.config?.tournamentName || 'Sans nom');
          importState(data.state);
        }
      } else {
        // Load most recent tournament
        const data = await firebaseSyncService.loadLastTournament(currentUser.email);
        if (data && data.state) {
          console.log('📋 Tournoi chargé - ID:', data.id, '| Nom:', data.state?.config?.tournamentName || 'Sans nom');
          importState(data.state);
        }
      }
    } catch (error) {
      console.error('❌ Error loading tournament:', error);
    } finally {
      setLoadingTournament(false);
    }
  };

  const handleLoginClose = () => {
    setShowLoginModal(false);
  };

  const handleTournamentsClick = () => {
    if (isSignedIn()) {
      setShowTournamentListModal(true);
    } else {
      setShowAuthRequiredModal(true);
    }
  };

  const handleTournamentLoad = (data) => {
    if (data && data.state) {
      importState(data.state);
      console.log('✅ Tournament loaded from modal:', data.state?.config?.tournamentName || 'Sans nom');
    }
  };

  const handleQRCodeClick = () => {
    setShowQRCodeModal(true);
  };

  const handleExportPDFClick = () => {
    setShowExportPDFModal(true);
  };

  const handleResetClick = () => {
    setShowResetModal(true);
  };

  const handleResetConfirm = async () => {
    firebaseSyncService.clearCurrentTournament();

    // Build template state
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const hours = String(today.getHours()).padStart(2, '0');
    const minutes = String(today.getMinutes()).padStart(2, '0');
    const seconds = String(today.getSeconds()).padStart(2, '0');
    const tournamentName = `Tournoi ${day}/${month} ${hours}:${minutes}:${seconds}`;

    const defaultPlayers = Array.from({ length: 8 }, (_, i) => ({
      id: i,
      name: `Joueur ${i + 1}`,
      position: i % 2 === 0 ? 'left' : 'right',
      fixedPartnerId: null,
      played: 0, wins: 0, draws: 0, losses: 0,
      setsWon: 0, setsLost: 0, gamesWon: 0, gamesLost: 0, points: 0
    }));

    const defaultCourts = [
      { id: 0, name: 'Terrain 1' },
      { id: 1, name: 'Terrain 2' }
    ];

    const templateState = {
      config: {
        ...DEFAULT_CONFIG,
        tournamentName,
      },
      players: defaultPlayers,
      courts: defaultCourts,
      schedule: { rounds: [], hasRepetitions: false, warnings: [] },
      currentRound: 0,
      scheduleGenerated: false,
      tournamentStarted: false,
      tournamentFinished: false,
      startTime: null,
      finalMatch: null
    };

    // Apply template state immediately (stable setters, no stale closure)
    importState(templateState);

    // Save to Firebase if signed in
    if (isSignedIn() && currentUser) {
      try {
        const newTournamentId = await firebaseSyncService.createNewTournament(
          templateState,
          currentUser.email,
          tournamentName
        );
        if (newTournamentId) {
          console.log('✅ Nouveau tournoi créé - ID:', newTournamentId);
        }
      } catch (error) {
        console.error('❌ Erreur lors de la création du nouveau tournoi:', error);
      }
    }
  };

  const handleSignOutClick = () => {
    setShowSignOutModal(true);
  };

  const handleSignOutConfirm = async () => {
    setSignOutLoading(true);

    // Save tournament before signing out if user is signed in
    if (isSignedIn() && firebaseSyncService.currentTournamentId) {
      try {
        const state = {
          config: {},
          players: [],
          schedule: {},
          // Export full state here if needed
        };
        await firebaseSyncService.saveTournament(state, currentUser.email, 'auto-save');
      } catch (error) {
        console.error('Error saving before sign out:', error);
      }
    }

    const result = await signOut();

    if (result.success) {
      setShowSignOutModal(false);
      reset();
      setShowLoginModal(true);
    }

    setSignOutLoading(false);
  };

  const handleGuestModeSwitch = () => {
    switchToLoginMode();
    setShowLoginModal(true);
  };

  // Show loading spinner while checking auth or loading tournament from Firebase
  if (loading || loadingTournament) {
    return (
      <div className="App">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Chargement...</span>
            </div>
            <p className="text-muted">Chargement de l'application...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <LoginBanner onLoginClick={() => setShowLoginModal(true)} />
      <ScrollWizardPage
        onTournamentsClick={handleTournamentsClick}
        onResetClick={handleResetClick}
        onQRCodeClick={handleQRCodeClick}
        onExportPDFClick={handleExportPDFClick}
        onSignOutClick={currentUser ? handleSignOutClick : handleGuestModeSwitch}
        onLoginClick={() => setShowLoginModal(true)}
        isSyncing={isSaving}
        lastSyncTime={lastSaveTime}
      />

      {/* Modals */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={handleLoginClose}
      />

      <SignOutModal
        isOpen={showSignOutModal}
        onClose={() => setShowSignOutModal(false)}
        onConfirm={handleSignOutConfirm}
        loading={signOutLoading}
      />

      <TournamentListModal
        isOpen={showTournamentListModal}
        onClose={() => setShowTournamentListModal(false)}
        onLoad={handleTournamentLoad}
      />

      <QRCodeModal
        isOpen={showQRCodeModal}
        onClose={() => setShowQRCodeModal(false)}
      />

      <ExportPDFModal
        isOpen={showExportPDFModal}
        onClose={() => setShowExportPDFModal(false)}
      />

      <AuthRequiredModal
        isOpen={showAuthRequiredModal}
        onClose={() => setShowAuthRequiredModal(false)}
      />

      <ConfirmModal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        onConfirm={handleResetConfirm}
        title="Nouveau tournoi"
        message="Créer un nouveau tournoi ? Toutes les données non sauvegardées seront perdues."
        confirmLabel="Créer"
        confirmVariant="danger"
        icon="bi-arrow-clockwise"
      />

      {/* Toast Notifications */}
      <ToastContainer />
    </div>
  );
}

function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <TournamentProvider>
          <AppContent />
        </TournamentProvider>
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;
